'use client';

import React from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    defaultHeading:
      'Use it with your existing ticketing system with 0 operational change',
    setUpWith: 'Set up Open with',
    point1: 'Set up in under an hour.',
    point2:
      'Integrates into your current support channels—tickets, email, live chat, and more.',
    point3:
      'Follows your existing assignment rules, automations, and reporting.',
    point4: 'Escalates to agents in your preferred inbox.',
    runCompatibility: 'Run compatibility test',
    seeAllHelpdesks: 'See all supported helpdesks',
    useAsMainApp: 'I want to use Open as my main app',
  },
  ar: {
    defaultHeading:
      'استخدمه مع نظام التذاكر اللي عندك بلا أي تغيير في طريقة عملك',
    setUpWith: 'فعّل Open مع',
    point1: 'جاهز في أقل من ساعة.',
    point2:
      'يندمج مع قنوات الدعم اللي عندك — تذاكر، إيميل، دردشة مباشرة، وأكثر.',
    point3: 'يشتغل وفق قواعد التوزيع والأتمتة والتقارير اللي عندك.',
    point4: 'يحوّل للوكلاء في صندوق الوارد اللي تختاره.',
    runCompatibility: 'شغّل اختبار التوافق',
    seeAllHelpdesks: 'شوف كل المنصات المدعومة',
    useAsMainApp: 'أريد استخدام Open كتطبيقي الأساسي',
  },
} as const;

interface GalleryImage {
  src: string;
  name: string;
  rotation: number;
  translateY?: number;
  translateX?: number;
  zIndex?: number;
  href: string;
}

const galleryImages: GalleryImage[] = [
  {
    src: '/helpdicks/hubspot.png',
    name: 'HubSpot',
    rotation: -19,
    translateX: -200,
    href: '/helpdesks/hubspot',
  },
  {
    src: '/helpdicks/intercom.png',
    name: 'Intercom',
    rotation: -19,
    translateY: 2,
    translateX: -104,
    href: '/helpdesks/intercom',
  },
  {
    src: '/helpdicks/zendesk.png',
    name: 'Zendesk',
    rotation: 0,
    translateY: -32,
    zIndex: 10,
    href: '/helpdesks/zendesk',
  },
  {
    src: '/helpdicks/salesforce.png',
    name: 'Salesforce',
    rotation: 12,
    translateX: 104,
    translateY: 2,
    href: '/helpdesks/salesforce',
  },
  {
    src: '/helpdicks/freshdesk.png',
    name: 'Freshdesk',
    rotation: 12,
    translateX: 200,
    href: '/helpdesks/freshdesk',
  },
];

interface Feature4Props {
  heading?: string;
}

export const Feature4 = ({ heading }: Feature4Props) => {
  const { locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const resolvedHeading = heading ?? c.defaultHeading;
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  return (
    <section
      id="feature4"
      className="from-background to-muted/50 bg-gradient-to-b py-16 md:py-28 lg:py-32"
    >
      <div className="container overflow-x-clip">
        <motion.div
          initial={{ opacity: 0, translateY: 100 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.7,
            type: 'spring',
            bounce: 0.3,
          }}
          className="relative flex h-42 items-center justify-center"
        >
          {galleryImages.map((image, index) => (
            <motion.a
              href={image.href}
              key={index}
              initial={{
                rotate: image.rotation,
                translateY: image.translateY || 0,
                translateX: 0,
                zIndex: image.zIndex || 'auto',
              }}
              animate={{
                rotate: image.rotation,
                translateY: image.translateY || 0,
                translateX: image.translateX || 0,
                zIndex: image.zIndex || 'auto',
              }}
              drag
              dragSnapToOrigin
              whileHover={{
                scale: 1.15,
                rotate: 0,
                zIndex: 999,
                translateY: -20,
              }}
              transition={{
                duration: 0.7,
                bounce: 0.3,
                type: 'spring',
                delay: 0.5,
                translateY: { duration: 0.2 },
                rotate: { duration: 0.2 },
                scale: { duration: 0.2 },
                zIndex: { duration: 0.2 },
              }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              className="group absolute size-42 cursor-pointer overflow-hidden rounded-3xl shadow-xl transition-all ease-out"
            >
              <Image
                src={image.src}
                alt={image.name}
                fill
                unoptimized
                className={`pointer-events-none object-cover transition-all duration-500 ease-out ${
                  hoveredIndex !== null && hoveredIndex !== index
                    ? 'blur-[2px] grayscale-[50%]'
                    : hoveredIndex === index
                      ? 'blur-0 grayscale-0'
                      : 'blur-0 grayscale-[50%]'
                }`}
              />
            </motion.a>
          ))}

          {/* Info Card */}
          <AnimatePresence>
            {hoveredIndex !== null && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onHoverStart={() => setHoveredIndex(hoveredIndex)}
                onHoverEnd={() => setHoveredIndex(null)}
                className="pointer-events-auto absolute -top-80 left-1/2 z-[1000] w-full max-w-md -translate-x-1/2"
              >
                <div>
                  <Card className="overflow-hidden border-2 shadow-2xl">
                    <CardHeader className="pb-4">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          {c.setUpWith}
                          <span className="inline-flex items-center gap-1.5">
                            <span className="relative inline-block size-6 shrink-0 overflow-hidden rounded-full bg-white shadow-sm ring-1 ring-black/5">
                              <Image
                                src={galleryImages[hoveredIndex].src}
                                alt={galleryImages[hoveredIndex].name}
                                fill
                                unoptimized
                                className="object-cover"
                              />
                            </span>
                            {galleryImages[hoveredIndex].name}
                          </span>
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                            <Check className="size-3 text-green-600" />
                          </div>
                          <p className="text-muted-foreground text-sm">
                            {c.point1}
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                            <Check className="size-3 text-green-600" />
                          </div>
                          <p className="text-muted-foreground text-sm">
                            {c.point2}
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                            <Check className="size-3 text-green-600" />
                          </div>
                          <p className="text-muted-foreground text-sm">
                            {c.point3}
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                            <Check className="size-3 text-green-600" />
                          </div>
                          <p className="text-muted-foreground text-sm">
                            {c.point4}
                          </p>
                        </div>
                      </div>
                      <div className="pt-2">
                        <a
                          href="#"
                          className="text-primary hover:text-primary/80 pointer-events-auto inline-flex items-center gap-1.5 text-sm font-medium underline-offset-4 transition-colors hover:underline"
                        >
                          {c.runCompatibility}
                          <ArrowRight className="size-4" />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, translateY: -20 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.3,
            delay: 0.5,
          }}
          className="mt-10 flex flex-col items-center justify-center"
        >
          <h3 className="max-w-md text-center text-2xl">{resolvedHeading}</h3>
          <div className="mt-10 flex flex-col items-center justify-center gap-4">
            <Button
              asChild
              className="group flex items-center justify-center gap-2 rounded-full border px-4 py-1 tracking-tight"
            >
              <Link href="/helpdesks">
                {c.seeAllHelpdesks}
                <ArrowRight className="size-4 -rotate-45 transition-all ease-out group-hover:rotate-0" />
              </Link>
            </Button>
            <Link
              href="/helpdesks/open"
              className="text-muted-foreground hover:text-foreground group inline-flex items-center gap-1.5 text-sm underline underline-offset-4 transition-colors"
            >
              {c.useAsMainApp}
              <ArrowRight className="size-4 transition-all ease-out group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
